import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { chatApi } from '../api/chat';
import type { ChatRolloutSite, ChatRolloutStatus } from '../types';

/**
 * Where every franchise stands in the changeover from Digital Air Strike to the
 * new chat. Two signals per site, deliberately kept apart:
 *
 *   - the status someone set here, and
 *   - "seen on site", stamped when the widget actually loads there.
 *
 * The second is the one that can't be wrong, which is what keeps this honest
 * across ~146 locations instead of decaying into stale bookkeeping.
 */

const LABEL: Record<ChatRolloutStatus, string> = {
  not_started: 'Not started',
  ready: 'Ready to install',
  pilot: 'Pilot',
  live: 'Live',
  paused: 'Paused',
};

const COLOR: Record<ChatRolloutStatus, { bg: string; fg: string }> = {
  not_started: { bg: '#f1f5f9', fg: '#64748b' },
  ready: { bg: '#e0e7ff', fg: '#3730a3' },
  pilot: { bg: '#fef3c7', fg: '#92400e' },
  live: { bg: '#dcfce7', fg: '#15803d' },
  paused: { bg: '#fee2e2', fg: '#b91c1c' },
};

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function ChatRolloutPage() {
  // The menu entry is already admin-only, but the route was reachable by URL
  // for any signed-in user — an empty page rather than a leak (the API is
  // adminOnly), still not something to leave open.
  const { user, isLoading: authLoading } = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ['chat-rollout'],
    queryFn: () => chatApi.rollout(),
  });
  const [filter, setFilter] = useState<'all' | ChatRolloutStatus>('all');
  const [q, setQ] = useState('');

  const sites = useMemo(() => {
    const all = data?.sites ?? [];
    return all.filter((s) => {
      if (filter !== 'all' && s.rolloutStatus !== filter) return false;
      if (!q.trim()) return true;
      const hay = `${s.af} ${s.name} ${s.location}`.toLowerCase();
      return hay.includes(q.trim().toLowerCase());
    });
  }, [data, filter, q]);

  if (!authLoading && user && user.role !== 'admin') return <Navigate to="/" replace />;
  if (isLoading) return <div style={{ padding: 24 }}>Loading rollout status…</div>;
  if (error) return <div style={{ padding: 24 }}>Couldn’t load the tracker: {(error as Error).message}</div>;

  const s = data!.summary;
  const done = s.live;
  const pct = s.total ? Math.round((done / s.total) * 100) : 0;

  return (
    <div style={{ padding: '22px 24px 70px', maxWidth: 1200, margin: '0 auto' }}>
      <p style={{ margin: 0 }}>
        <Link to="/" style={{ color: '#2563eb' }}>← Dashboard</Link>
      </p>
      <h1 style={{ margin: '10px 0 4px', fontSize: '1.5rem' }}>Chat rollout</h1>
      <p style={{ color: '#64748b', marginTop: 0 }}>
        Which franchise websites have switched from Digital Air Strike to the new chat.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '18px 0 8px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 240 }}>
          <div style={{ height: 10, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: '#15803d' }} />
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
            <strong style={{ color: '#15803d' }}>{done}</strong> of {s.total} live ({pct}%) ·{' '}
            {s.seenLive} confirmed by their own website
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '14px 0' }}>
        {(['all', 'not_started', 'ready', 'pilot', 'live', 'paused'] as const).map((k) => {
          const count = k === 'all' ? s.total
            : k === 'not_started' ? s.notStarted
            : k === 'ready' ? s.ready
            : k === 'pilot' ? s.pilot
            : k === 'live' ? s.live
            : s.paused;
          const on = filter === k;
          return (
            <button
              key={k}
              onClick={() => setFilter(k)}
              style={{
                border: on ? '2px solid #1f2a48' : '1px solid #cbd5e1',
                background: on ? '#1f2a48' : '#fff',
                color: on ? '#fff' : '#1f2a48',
                borderRadius: 999, padding: '6px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              {k === 'all' ? 'All' : LABEL[k]} ({count})
            </button>
          );
        })}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search AF, name or city…"
          style={{ padding: '7px 11px', border: '1px solid #cbd5e1', borderRadius: 6, minWidth: 220 }}
        />
      </div>

      <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'auto', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr>
              {['AF', 'Franchise', 'Status', 'Chat on', 'Team', 'Seen on site', 'DMR call', 'Website'].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: 'left', padding: '9px 12px', borderBottom: '1px solid #e2e8f0',
                    fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b',
                    background: '#fafafa', position: 'sticky', top: 0,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sites.map((site) => <Row key={site.af} site={site} />)}
            {sites.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 18, color: '#64748b' }}>No locations match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ site }: { site: ChatRolloutSite }) {
  const c = COLOR[site.rolloutStatus];
  return (
    <tr>
      <td style={td}>
        <Link to={`/?af=${encodeURIComponent(site.af)}&tab=tab-chat`} style={{ color: '#2563eb', fontWeight: 600 }}>
          {site.af}
        </Link>
        {site.isTest && (
          <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 999, padding: '1px 7px' }}>
            TEST
          </span>
        )}
      </td>
      <td style={td}>
        <div style={{ fontWeight: 600 }}>{site.name}</div>
        <div style={{ fontSize: 12, color: '#64748b' }}>{site.location}</div>
      </td>
      <td style={td}>
        <span style={{ background: c.bg, color: c.fg, borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {LABEL[site.rolloutStatus]}
        </span>
      </td>
      <td style={td}>{site.enabled ? 'Yes' : <span style={{ color: '#94a3b8' }}>No</span>}</td>
      <td style={td}>
        {site.agents > 0 ? site.agents : <span style={{ color: '#b45309' }}>none</span>}
      </td>
      <td style={td}>
        {site.embedLastSeenAt
          ? <span title={site.embedLastSeenAt}>{fmtDate(site.embedLastSeenAt)}</span>
          : <span style={{ color: '#94a3b8' }}>never</span>}
      </td>
      {/* The rollout runs through the DMR calls (#246): plan the install around the call. */}
      <td style={td}>
        {site.dmrCall ? (
          <span title={site.dmrCall.at} style={{ color: site.dmrCall.status === 'completed' ? '#15803d' : undefined }}>
            {site.dmrCall.status === 'completed' ? 'Done ' : ''}{fmtDate(site.dmrCall.at)}
          </span>
        ) : (
          <span style={{ color: '#94a3b8' }}>Not booked</span>
        )}
      </td>
      <td style={td}>
        {site.siteUrl
          ? <a href={site.siteUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>visit</a>
          : '—'}
      </td>
    </tr>
  );
}

const td: React.CSSProperties = { padding: '9px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };
